import { useEffect, useRef, useState } from 'react';
import type { Idioma } from '@matias-asset-os/i18n';

/**
 * DICTAR EN VEZ DE TECLEAR.
 *
 * Lo que se le cuenta al asistente se cuenta saliendo de una visita o entre
 * dos llamadas, con el móvil en la mano: «hablé con Pouline, está analizando
 * la oferta» se dice en tres segundos y se teclea en treinta.
 *
 * Usa el reconocimiento de voz del propio navegador —Edge, Chrome y Safari lo
 * traen—, así que no hace falta otra clave ni otro proveedor, y el audio no
 * pasa por nuestro servidor. Lo dictado se escribe en el campo y NO se envía
 * solo: un nombre mal oído («Pauline» por «Pouline») se corrige antes de
 * mandarlo, que es más barato que deshacer una nota en la ficha equivocada.
 */

interface ResultadoVoz {
  isFinal: boolean;
  0: { transcript: string };
}
interface Reconocedor {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { results: ArrayLike<ResultadoVoz> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type ClaseReconocedor = new () => Reconocedor;

function claseDeDictado(): ClaseReconocedor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: ClaseReconocedor;
    webkitSpeechRecognition?: ClaseReconocedor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const VOZ: Record<Idioma, string> = { es: 'es-ES', en: 'en-GB', de: 'de-DE' };

/** Lo dictado, a continuación de lo que ya estaba escrito. */
export function unirDictado(base: string, dictado: string): string {
  const antes = base.replace(/\s+$/, '');
  const dicho = dictado.replace(/\s+/g, ' ').trim();
  if (!dicho) return base;
  return antes ? `${antes} ${dicho}` : dicho;
}

export type ErrorDictado = 'noSoportado' | 'permiso' | 'sinVoz' | 'red' | 'otro';

export function errorDeDictado(codigo: string): ErrorDictado | null {
  if (codigo === 'aborted') return null; // lo paró quien dictaba
  if (codigo === 'not-allowed' || codigo === 'service-not-allowed') return 'permiso';
  if (codigo === 'no-speech') return 'sinVoz';
  if (codigo === 'network') return 'red';
  return 'otro';
}

export function useDictado(idioma: Idioma, onTexto: (texto: string) => void) {
  const [escuchando, setEscuchando] = useState(false);
  const [error, setError] = useState<ErrorDictado | null>(null);
  const reconocedor = useRef<Reconocedor | null>(null);

  // Salir de la pantalla con el micrófono abierto lo cierra.
  useEffect(() => () => reconocedor.current?.abort(), []);

  function empezar(base: string) {
    const Clase = claseDeDictado();
    if (!Clase) {
      setError('noSoportado');
      return;
    }
    setError(null);
    const r = new Clase();
    r.lang = VOZ[idioma];
    /*
      En el móvil, Chrome repite las frases en modo continuo. Allí se dicta de
      frase en frase —para solo al callarse— y en el ordenador se sigue
      escuchando hasta que se pulsa otra vez.
    */
    r.continuous = !window.matchMedia?.('(pointer: coarse)').matches;
    r.interimResults = true;
    r.onresult = (e) => {
      // Se rehace desde el principio en cada evento: sumar solo lo nuevo
      // duplica frases en algunos navegadores.
      let dicho = '';
      for (let i = 0; i < e.results.length; i += 1) dicho += `${e.results[i]![0].transcript} `;
      onTexto(unirDictado(base, dicho));
    };
    r.onerror = (e) => setError(errorDeDictado(e.error));
    r.onend = () => {
      setEscuchando(false);
      reconocedor.current = null;
    };
    reconocedor.current = r;
    try {
      r.start();
      setEscuchando(true);
    } catch {
      setError('otro');
    }
  }

  function parar() {
    reconocedor.current?.stop();
  }

  return { escuchando, error, empezar, parar };
}
